import { useCallback, useEffect, useRef, useState } from "react";
import { alfredService, AlfredQuote } from "src/api";
import { useUrlParamsContext } from "src/contexts";
import { debounce } from "src/utils";

interface UseAlfredQuotePreviewParams {
  sourceCurrency: string | null;
  targetCurrency: string | null;
  amount: string;
}

/**
 * Live quote-preview hook. On every (debounced 400 ms) amount change
 * fetches the stable-equivalent + fee breakdown from Alfred so the
 * widget can show "You'll receive ≈ X USDC" and convert the value into
 * USDC for our platform-side fee/limit checks.
 *
 * Returns `null` when amount is empty / non-positive — callers should
 * skip platform validation in that state and treat it as "no quote yet".
 */
export const useAlfredQuotePreview = ({
  sourceCurrency,
  targetCurrency,
  amount,
}: UseAlfredQuotePreviewParams) => {
  const { paymentRouteId } = useUrlParamsContext();
  const [quote, setQuote] = useState<AlfredQuote | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // `debounce` from src/utils returns a fn that resolves the latest
  // call only — perfect for "user keeps typing" so we don't render
  // an outdated quote. `payment_route_id` is forwarded so the
  // backend can read `route.meta.destination_chain` and pin Alfred's
  // /quotes to the real network fee — without it the preview comes
  // back with a conservative max fee that disagrees with /onramp at
  // submit time (e.g. 40 BRL → preview 5.46 USDC vs real 7.22).
  const fetchQuote = useCallback(
    debounce(400, async (s: string, t: string, a: string, route: string) => {
      return await alfredService.getQuotePreview({
        source_currency_id: s,
        target_currency_id: t,
        from_amount: a,
        payment_route_id: route || undefined,
      });
    }),
    []
  );

  // Track the latest request id so a slow earlier response can't
  // overwrite a newer state.
  const requestIdRef = useRef(0);

  useEffect(() => {
    const numeric = Number(amount);
    if (!sourceCurrency || !targetCurrency || !amount || !Number.isFinite(numeric) || numeric <= 0) {
      setQuote(null);
      setError(null);
      setLoading(false);
      return;
    }

    const id = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    fetchQuote(sourceCurrency, targetCurrency, amount, paymentRouteId ?? "")
      .then((q) => {
        if (id !== requestIdRef.current) return;
        setQuote(q);
      })
      .catch((err) => {
        if (id !== requestIdRef.current) return;
        console.error("Failed to fetch Alfred quote preview:", err);
        setError(String(err));
        setQuote(null);
      })
      .finally(() => {
        if (id !== requestIdRef.current) return;
        setLoading(false);
      });
  }, [sourceCurrency, targetCurrency, amount, paymentRouteId, fetchQuote]);

  // Quote countdown — Alfred quotes carry an ISO `expiration` (~1 h
  // out). We tick every second so the widget can render "Quote
  // refreshes in 0:54", and once it hits zero we trigger a refetch
  // automatically so the user never submits a stale quote.
  const [secondsUntilExpiry, setSecondsUntilExpiry] = useState<number | null>(
    null,
  );
  useEffect(() => {
    if (!quote?.expiration) {
      setSecondsUntilExpiry(null);
      return;
    }
    const tick = () => {
      const remaining = Math.max(
        0,
        Math.floor((new Date(quote.expiration).getTime() - Date.now()) / 1000),
      );
      setSecondsUntilExpiry(remaining);
      if (remaining === 0) {
        // Force a refetch by bumping the request id; the effect above
        // re-runs because we depend on `quote` ref change after this
        // setQuote(null), which retriggers the fetch flow on the next
        // render with the same amount inputs.
        requestIdRef.current += 1;
        setQuote(null);
        const numeric = Number(amount);
        if (
          sourceCurrency &&
          targetCurrency &&
          amount &&
          Number.isFinite(numeric) &&
          numeric > 0
        ) {
          setLoading(true);
          fetchQuote(
            sourceCurrency,
            targetCurrency,
            amount,
            paymentRouteId ?? "",
          )
            .then((q) => setQuote(q))
            .catch((err) => setError(String(err)))
            .finally(() => setLoading(false));
        }
      }
    };
    tick();
    const handle = setInterval(tick, 1000);
    return () => clearInterval(handle);
  }, [
    quote?.expiration,
    amount,
    sourceCurrency,
    targetCurrency,
    paymentRouteId,
    fetchQuote,
  ]);

  return { quote, loading, error, secondsUntilExpiry };
};
